import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { formatQuantity } from "@/lib/quantity-format";
import type {
  ReportComparison,
  ReportComparisonColumn,
  ReportFilter,
} from "@/types/production-report.types";

// Spec 084 (FR-017a · FR-017b) — o QUADRO comparativo de uma característica.
//
// Uma coluna por valor COM registro: valor sem batelada no período não aparece zerado, porque zero
// afirmaria que aquele valor produziu nada quando o que houve foi não ter havido batelada dele.
// "Sem base" continua "sem base", nunca 0% (mesma regra da Spec 083).
//
// A tabela rola na horizontal dentro do próprio bloco: até dez colunas não cabem em tela de
// notebook, e deixar a página inteira rolar de lado levaria a régua de indicadores junto.

interface Props {
  comparison: ReportComparison | null;
  filter: ReportFilter | null;
  lineId: string;
  unit: string;
  heroId: string | null;
}

const pct = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 });

export function CharacteristicComparison({ comparison, filter, lineId, unit, heroId }: Props) {
  if (!comparison) return null;
  const colunas = comparison.columns.filter((c) => c.stages.some((s) => s.lineId === lineId));
  if (colunas.length === 0) {
    return (
      <p className="text-[12px] text-text-secondary">
        Nenhuma batelada desta etapa no período tem {comparison.characteristicName} para comparar.
      </p>
    );
  }
  const indicadores = indicadoresDo(colunas, lineId, heroId);

  return (
    <section aria-labelledby={`quadro-${lineId}`} className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id={`quadro-${lineId}`} className="text-[13px] font-medium text-text-primary">
          Por {comparison.characteristicName}
        </h3>
        {filter ? (
          <span className="text-[11px] text-text-tertiary">
            O quadro compara o período inteiro, sem o recorte de {filter.characteristicName}.
          </span>
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Item</TableHeaderCell>
              {colunas.map((c) => (
                <TableHeaderCell key={c.key} className="text-right">
                  {c.label}
                  <span className="ml-1 text-[10px] font-normal text-text-tertiary">
                    ({c.runs} {c.runs === 1 ? "batelada" : "bateladas"})
                  </span>
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <Linha
              rotulo={`Entrada (${unit})`}
              colunas={colunas}
              lineId={lineId}
              valor={(s) => s.input.quantity}
            />
            <Linha
              rotulo={`Saída útil (${unit})`}
              colunas={colunas}
              lineId={lineId}
              valor={(s) => soma(s.products)}
            />
            <Linha
              rotulo={`Perdas (${unit})`}
              colunas={colunas}
              lineId={lineId}
              valor={(s) => soma(s.losses)}
            />
            {indicadores.map((ind) => (
              <Linha
                key={ind.id}
                rotulo={ind.name}
                colunas={colunas}
                lineId={lineId}
                percentual
                valor={(s) => s.indicators.find((i) => i.id === ind.id)?.value ?? null}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

type ColumnStage = ReportComparisonColumn["stages"][number];

function Linha({
  rotulo,
  colunas,
  lineId,
  valor,
  percentual = false,
}: {
  rotulo: string;
  colunas: ReportComparisonColumn[];
  lineId: string;
  valor: (stage: ColumnStage) => number | null;
  percentual?: boolean;
}) {
  return (
    <TableRow>
      <TableCell className="text-text-secondary">{rotulo}</TableCell>
      {colunas.map((c) => {
        const stage = c.stages.find((s) => s.lineId === lineId);
        const v = stage ? valor(stage) : null;
        return (
          <TableCell key={c.key} className="text-right font-mono tabular-nums">
            {v === null ? "—" : percentual ? `${pct.format(v)}%` : formatQuantity(v)}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

/** Zero registros não vira zero: só soma quando há item, e devolve nulo quando não há. */
function soma(itens: Array<{ quantity: number }>): number | null {
  return itens.length === 0 ? null : itens.reduce((acc, i) => acc + i.quantity, 0);
}

/** O indicador em destaque primeiro; os demais na ordem em que a primeira coluna os traz. */
function indicadoresDo(
  colunas: ReportComparisonColumn[],
  lineId: string,
  heroId: string | null
): Array<{ id: string; name: string }> {
  const vistos = new Map<string, string>();
  for (const c of colunas) {
    for (const i of c.stages.find((s) => s.lineId === lineId)?.indicators ?? []) {
      if (!vistos.has(i.id)) vistos.set(i.id, i.name);
    }
  }
  const todos = [...vistos].map(([id, name]) => ({ id, name }));
  return todos.sort((a, b) => Number(b.id === heroId) - Number(a.id === heroId));
}
